<template>
  <nav class="bg-white border-b border-gray-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <div class="flex">
          <div class="flex-shrink-0 flex items-center">
            <router-link to="/dashboard" class="flex items-center">
              <img src="@/assets/qrky-logo.png" alt="QRKY" class="h-[17px] w-auto" />
            </router-link>
          </div>
          <div class="hidden sm:ml-8 sm:flex sm:space-x-8">
            <router-link
              to="/dashboard"
              class="nav-link"
              :class="{ 'border-purple-500 text-gray-900': $route.path === '/dashboard' }"
            >
              Dashboard
            </router-link>
            <router-link
              to="/qrcodes"
              class="nav-link"
              :class="{ 'border-purple-500 text-gray-900': $route.path.startsWith('/qrcodes') }"
            >
              QR Codes
            </router-link>
            <router-link
              to="/folders"
              class="nav-link"
              :class="{ 'border-purple-500 text-gray-900': $route.path === '/folders' }"
            >
              Folders
            </router-link>
            <router-link
              to="/pricing"
              class="nav-link"
              :class="{ 'border-purple-500 text-gray-900': $route.path === '/pricing' }"
            >
              Pricing
            </router-link>
            <router-link
              to="/api-keys"
              class="nav-link"
              :class="{ 'border-purple-500 text-gray-900': $route.path === '/api-keys' || $route.path === '/api-pricing' }"
            >
              API
            </router-link>
            <button
              @click="$emit('openContact')"
              class="nav-link inline-flex items-center gap-1"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Contact Us
            </button>
          </div>
        </div>
        
        <div class="flex items-center space-x-4">
          <router-link
            to="/qrcodes/create"
            class="btn-primary"
          >
            + Create QR Code
          </router-link>
          
          <div ref="dropdownRef" class="relative" @click="showDropdown = !showDropdown">
            <button class="flex items-center space-x-2 text-gray-700 hover:text-gray-900">
              <div class="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <span class="text-purple-600 font-semibold">
                  {{ userInitials }}
                </span>
              </div>
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <div
              v-if="showDropdown"
              class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
            >
              <router-link
                to="/profile"
                class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                @click="showDropdown = false"
              >
                Profile
              </router-link>
              <router-link
                to="/subscription"
                class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                @click="showDropdown = false"
              >
                Subscription & Credits
              </router-link>
              <router-link
                to="/api-keys"
                class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                @click="showDropdown = false"
              >
                API Keys
              </router-link>
              <a
                href="/api/v1/docs"
                target="_blank"
                class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                @click="showDropdown = false"
              >
                API Docs
              </a>
              <button
                @click="$emit('openContact'); showDropdown = false"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Contact Us
              </button>
              <hr class="my-1 border-gray-200" />
              <button
                @click="openDeleteModal"
                class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                Delete Account
              </button>
              <button
                @click="handleLogout"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>

  <!-- Delete Account Confirmation Modal -->
  <Teleport to="body">
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="fixed inset-0 bg-black bg-opacity-50" @click="closeDeleteModal"></div>
      <div class="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-2">Delete Your Account</h3>
        <p class="text-sm text-gray-600 mb-4">
          This action is <span class="font-semibold text-red-600">permanent</span> and cannot be undone. You will lose access to your account immediately.
        </p>

        <div v-if="deleteWarningsLoading" class="flex justify-center py-4">
          <svg class="animate-spin h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
        </div>

        <div v-else>
          <!-- Subscription warnings -->
          <div v-if="deleteWarnings.length > 0" class="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p class="text-sm font-medium text-yellow-800 mb-2">Active subscriptions that will be cancelled:</p>
            <ul class="space-y-1">
              <li v-for="warning in deleteWarnings" :key="warning.type" class="text-sm text-yellow-700 flex items-start gap-2">
                <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span>{{ warning.label }} ({{ warning.status }})</span>
              </li>
            </ul>
          </div>

          <!-- QR code count -->
          <div v-if="deleteQRCount > 0" class="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p class="text-sm text-red-700">
              <span class="font-medium">{{ deleteQRCount }}</span> QR code{{ deleteQRCount !== 1 ? 's' : '' }} and all associated scan data will be permanently deleted.
            </p>
          </div>

          <!-- Confirmation input -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Type <span class="font-mono font-bold text-red-600">DELETE</span> to confirm
            </label>
            <input
              v-model="deleteConfirmText"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="DELETE"
              autocomplete="off"
            />
          </div>

          <div v-if="deleteError" class="mb-4 text-sm text-red-600">{{ deleteError }}</div>

          <!-- Action buttons -->
          <div class="flex justify-end gap-3">
            <button
              @click="closeDeleteModal"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              @click="handleDeleteAccount"
              :disabled="deleteConfirmText !== 'DELETE' || deleting"
              class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ deleting ? 'Deleting...' : 'Delete Account' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

defineEmits(['openContact'])

const router = useRouter()
const authStore = useAuthStore()
const showDropdown = ref(false)
const dropdownRef = ref(null)

// Delete account state
const showDeleteModal = ref(false)
const deleteWarningsLoading = ref(false)
const deleteWarnings = ref([])
const deleteQRCount = ref(0)
const deleteConfirmText = ref('')
const deleteError = ref('')
const deleting = ref(false)

const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    showDropdown.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

const userInitials = computed(() => {
  const user = authStore.currentUser
  if (!user) return 'U'
  if (user.firstName && user.lastName) {
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
  }
  if (user.email) {
    return user.email[0].toUpperCase()
  }
  return 'U'
})

const handleLogout = () => {
  authStore.logout()
  router.push('/login')
}

const openDeleteModal = async () => {
  showDropdown.value = false
  showDeleteModal.value = true
  deleteWarningsLoading.value = true
  deleteConfirmText.value = ''
  deleteError.value = ''
  try {
    const data = await authStore.getDeleteAccountWarnings()
    deleteWarnings.value = data.warnings
    deleteQRCount.value = data.qrCodeCount
  } catch {
    deleteError.value = 'Failed to load account information.'
  } finally {
    deleteWarningsLoading.value = false
  }
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  deleteConfirmText.value = ''
  deleteError.value = ''
}

const handleDeleteAccount = async () => {
  if (deleteConfirmText.value !== 'DELETE') return
  deleting.value = true
  deleteError.value = ''
  try {
    await authStore.deleteAccount()
    showDeleteModal.value = false
    router.push('/login')
  } catch {
    deleteError.value = 'Failed to delete account. Please try again.'
  } finally {
    deleting.value = false
  }
}
</script>

<style scoped>
.nav-link {
  @apply inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors;
}
</style>
